import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { displayName } from '@wodi/domain';
import type { ImagingReportView, PatientDetail } from '@wodi/shared';
import { Button, LoadingBlock, Notice, Section } from '@wodi/ui';
import { useState } from 'react';

import { patientImagingReportsQuery } from '../api/imaging.ts';
import { can, sessionQuery } from '../api/session.ts';
import { formatInstant } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { modalityWords } from './format.ts';
import styles from './imaging.module.css';
import { PrintImagingReportDialog } from './prints.tsx';
import { ImagingReportBody, ReportStatusTag } from './report-view.tsx';

// Imaging reports on the patient record (docs/plans/phase2-radiology.md §6): newest first,
// the version in force with its state, earlier versions on request, and print.

export function PatientImagingReports({ patient }: { patient: PatientDetail }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const reports = useQuery(patientImagingReportsQuery(patient.id));
  const [printing, setPrinting] = useState<ImagingReportView | null>(null);
  const canPrint = session !== null && can(session, 'imaging.report.print');
  return (
    <Section title="Imaging reports">
      {reports.isPending ? <LoadingBlock label="Loading imaging reports" rows={3} /> : null}
      {reports.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void reports.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(reports.error, "Imaging reports couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {reports.isSuccess && reports.data.items.length === 0 ? (
        <p className={styles.meta}>No imaging reports.</p>
      ) : null}
      {reports.isSuccess && reports.data.items.length > 0 ? (
        <ul className={styles.list} aria-label="Imaging reports">
          {reports.data.items.map((report) => (
            <li key={report.id} className={styles.listItem}>
              <div className={styles.itemHead}>
                <span className={styles.stack}>
                  <strong>{report.itemName}</strong>
                  <span className={styles.meta}>
                    {`${modalityWords(report.modality)}${
                      report.performedAt === null
                        ? ''
                        : `, performed ${formatInstant(report.performedAt)}`
                    }, requested by ${report.orderingUser.displayName}`}
                  </span>
                </span>
                <span className={styles.actions}>
                  <ReportStatusTag report={report} />
                  {canPrint && report.enteredInError === null ? (
                    <Button
                      dense
                      aria-label={`Print ${report.itemName}`}
                      onPress={() => {
                        setPrinting(report);
                      }}
                    >
                      Print
                    </Button>
                  ) : null}
                </span>
              </div>
              <ImagingReportBody report={report} />
            </li>
          ))}
        </ul>
      ) : null}
      {printing === null || session === null ? null : (
        <PrintImagingReportDialog
          data={{
            facilityName: session.facility.name,
            patient: {
              displayName: displayName(patient.name),
              patientNumber: patient.patientNumber,
              sex: patient.sex.display,
              age: patient.age,
            },
            report: printing,
          }}
          onClose={() => {
            setPrinting(null);
          }}
        />
      )}
    </Section>
  );
}
